import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

const serviceBaseUrls = {
  events: process.env.NEXT_PUBLIC_API_URL_EVENTS,
  auth: process.env.NEXT_PUBLIC_API_URL_AUTH,
  orders: process.env.NEXT_PUBLIC_API_URL_ORDERS,
  account: process.env.NEXT_PUBLIC_API_URL_ACCOUNT,
  screens: process.env.NEXT_PUBLIC_API_URL_SCREENMS,
  coupons: process.env.NEXT_PUBLIC_API_URL_COUPON,
  donations: process.env.NEXT_PUBLIC_API_URL_DONATION,
  medialib: process.env.NEXT_PUBLIC_API_URL_MEDIALIB,
  signage: process.env.NEXT_PUBLIC_API_URL_SIGNAGE,
}

const dynamicBaseQuery = (args, api, extraOptions) => {
  const { service = 'events', ...requestArgs } = args
  const baseUrl = serviceBaseUrls[service]

  if (!baseUrl) {
    console.error(
      `[baseApi] Missing base URL for service: ${service}. Available services: ${Object.keys(
        serviceBaseUrls
      ).join(', ')} — ensure corresponding NEXT_PUBLIC_API_URL_* is configured.`
    )
    return Promise.resolve({
      error: {
        status: 400,
        data: {
          message: `Unknown service: ${service} (missing base URL)`,
          availableServices: Object.keys(serviceBaseUrls),
        },
      },
    })
  }

  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = localStorage.getItem('userToken')

      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }

      headers.set('Content-Type', 'application/json')

      return headers
    },
  })(requestArgs, api, extraOptions)
}

const baseQueryWithRetry = async (args, api, extraOptions) => {
  let result = await dynamicBaseQuery(args, api, extraOptions)

  if (result.error && result.error.status >= 500) {
    const maxRetries = 3
    let retryCount = 0

    while (retryCount < maxRetries && result.error) {
      const delay = Math.pow(2, retryCount) * 1000
      await new Promise((resolve) => setTimeout(resolve, delay))

      result = await dynamicBaseQuery(args, api, extraOptions)
      retryCount++
    }
  }

  if (result.error?.status === 401) {
    // Token might be expired, redirect to user portal
    localStorage.removeItem('userToken')

    // Clear the userToken cookie
    document.cookie = 'userToken=; path=/; max-age=0'

    const userPortalUrl = 'https://welcome.myababil.com'
    window.location.href = userPortalUrl
  }

  return result
}

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithRetry,

  tagTypes: [
    'Salah',
    'SalahSettings',
    'User',
    'Users',
    'Event',
    'Space',
    'Media',
    'Settings',
    'Campaign',
    'Donation',
    'Coupon',
    'Order',
    'Screen',
    'Playlist',
    'Role',
    'TeamMember',
    'TeamMemberActivity',
    'Announcement',
    'InviteUsers',
    'ShareSalah',
    'ShareSalahImage',
  ],

  // Cache configuration following best practices
  // keepUnusedDataFor: 60, // Keep unused data for 60 seconds
  // refetchOnMountOrArgChange: 30, // Refetch if data is older than 30 seconds
  // refetchOnFocus: false, // Disable refetch on focus (can be enabled per endpoint)
  refetchOnReconnect: true, // Refetch when connection is restored

  endpoints: () => ({}),
})

export default baseApi
